import { useForumType } from '@/components/hooks/useForumType';
import React, { use, createContext, useContext, useState, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { registerComponent } from '../../lib/vulcan-lib/components';
import { Link } from '../../lib/reactRouterWrapper';
import Headroom from '../../lib/react-headroom'
import IconButton from '@/lib/vendor/@material-ui/core/src/IconButton';
import TocIcon from '@/lib/vendor/@material-ui/icons/src/Toc';
import { useCurrentUserId, useGetCurrentUser } from '../common/withUser';
import { SidebarsContext } from './SidebarsWrapper';
import withErrorBoundary from '../common/withErrorBoundary';
import classNames from 'classnames';
import { AnalyticsContext, useTracking } from '../../lib/analyticsEvents';
import { forumHeaderTitleSetting, forumShortTitleSetting } from '@/lib/instanceSettings';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { useLocation } from '../../lib/routeUtil';
import SearchBar from "@/components/common/SearchBar";
import UsersMenu from "../users/UsersMenu";
import { LWUsersAccountMenu } from "../users/UsersAccountMenu";
import NotificationsMenuButton from "../notifications/NotificationsMenuButton";
import { ICON_ONLY_NAVIGATION_BREAKPOINT } from "@/components/common/TabNavigationMenu/NavigationStandalone";
import NavigationDrawer from "@/components/common/TabNavigationMenu/NavigationDrawer";
import { KarmaChangeNotifier } from "../users/karmaChanges/KarmaChangeNotifier";
import HeaderSubtitle from "@/components/common/HeaderSubtitle";
import { Typography } from "@/components/common/Typography";
import ForumIcon from "@/components/common/ForumIcon";
import { isHomeRoute } from '@/lib/routeChecks';
import NotificationsMenu from "../notifications/NotificationsMenu";
import { IsLlmChatSidebarOpenContext } from './Layout';
import { useIsOnGrayBackground } from '../hooks/useIsOnGrayBackground';
import FundraiserBanner from '../common/FundraiserBanner';
import { useCookiesWithConsent } from '../hooks/useCookiesWithConsent';
import { HIDE_FUNDRAISER_BANNER_COOKIE } from '@/lib/cookies/cookies';
import { useStyles, defineStyles } from '../hooks/useStyles';
import { usePrerenderablePathname } from '../next/usePrerenderablePathname';
import { HideNavigationSidebarContext } from './HideNavigationSidebarContextProvider';
import { useMutationNoCache } from '@/lib/crud/useMutationNoCache';
import { gql } from '@/lib/generated/gql-codegen';

/** Height of the fundraiser banner */
export const FUNDRAISER_BANNER_HEIGHT = 34;
export const FUNDRAISER_BANNER_HEIGHT_MOBILE = 32;
/** Height of top header (without fundraiser banner). On Book UI sites, this is for desktop only */
export const getHeaderHeight = () => 64;
/** Height of top header on mobile (without fundraiser banner). On Friendly UI sites, this is the same as the HEADER_HEIGHT */
const getMobileHeaderHeight = () => 56;


const textColorOverrideStyles = ({
  theme,
  color,
  contrastColor,
}: {
  theme: ThemeType,
  color: string,
  contrastColor?: string,
}) => ({
  color,
  boxShadow: 'none',
  "& .Header-titleLink": {
    color,
  },
  "& .HeaderSubtitle-subtitle": {
    color,
  },
  "& .SearchBar-searchIcon": {
    color,
  },
  "& .ais-SearchBox-input": {
    color,
  },
  "& .ais-SearchBox-input::placeholder": {
    color,
  },
  "& .KarmaChangeNotifier-starIcon": {
    color,
  },
  "& .ColoredNumber-gainedPoints": {
    color,
  },
  "& .NotificationsMenuButton-badge": {
    color,
  },
  "& .NotificationsMenuButton-buttonClosed": {
    color,
  },
  "& .MessagesMenuButton-buttonClosed": {
    color,
  },
  "& .UsersMenu-arrowIcon": {
    color,
  },
  "& .EAButton-variantContained": {
    backgroundColor: color,
    color: contrastColor,
    "&:hover": {
      backgroundColor: `color-mix(in oklab, ${color} 90%, ${contrastColor})`,
    },
  },
  "& .EAButton-greyContained": {
    backgroundColor: `color-mix(in oklab, ${color} 15%, ${contrastColor})`,
    color: color,
    "&:hover": {
      backgroundColor: `color-mix(in oklab, ${color} 10%, ${theme.palette.background.transparent}) !important`,
    },
  },
});

const LLM_CHAT_SIDEBAR_WIDTH = 500;

type HeaderHeightContextValue = {
  showFundraiserBanner: boolean;
};
const HeaderHeightContext = createContext<HeaderHeightContextValue>({showFundraiserBanner: true});

export const styles = defineStyles("Header", (theme: ThemeType) => ({
  appBar: {
    boxShadow: theme.palette.boxShadow.appBar,
    color: theme.palette.text.bannerAdOverlay,

    ...(theme.dark
      ? {
        background: theme.palette.panelBackground.bannerAdTranslucent,
        backdropFilter: 'blur(4px) brightness(1.1)',
        "&$blackBackgroundAppBar": {
          boxShadow: theme.palette.boxShadow.appBarDarkBackground,
          background: theme.palette.panelBackground.appBarDarkBackground,
        },
      } : {
        backgroundColor: theme.palette.header.background,
        backdropFilter: 'blur(4px)',
        "&$blackBackgroundAppBar": {
          boxShadow: theme.palette.boxShadow.appBar,
          background: theme.palette.header.background,
        },
      }
    ),
    position: "static",
    width: "100%",
    display: "flex",
    zIndex: 1100,
    boxSizing: "border-box",
    flexShrink: 0,
    flexDirection: "column",
  },
  toolbar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 16,
    paddingRight: 16,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: 24,
      paddingRight: 24,
    },
    minHeight: "var(--header-height)",
  },
  appBarDarkBackground: {
    ...textColorOverrideStyles({
      theme,
      color: "var(--header-text-color)",
      contrastColor: "var(--header-contrast-color)",
    }),
    "--header-text-color": theme.palette.text.alwaysWhite,
    "--header-contrast-color": theme.palette.text.alwaysBlack,
  },
  blackBackgroundAppBar: {},
  root: {
    // This height (including the breakpoint at xs/600px) is set by Headroom, and this wrapper (which surrounds
    // Headroom and top-pads the page) has to match.
    height: "var(--header-height)",
    "@media print": {
      display: "none"
    }
  },
  title: {
    display: "flex",
    overflow: "hidden",
    flexGrow: 1,
    flexShrink: 1,
    position: "relative",
    top: 3,
    color: theme.palette.text.secondary,
  },
  titleLink: {
    color: theme.palette.text.bannerAdOverlay,
    fontSize: 19,
    '&:hover, &:active': {
      textDecoration: 'none',
      opacity: 0.7,
    },
    display: 'flex',
    alignItems: 'center',
    height: '19px',
    
    ...(theme.isAF && {
      top: 0,
    }),
  },
  menuButton: {
    marginLeft: -8,
    marginRight: 8,
  },
  icon: {
    width: 24,
    height: 24,
  },
  hideLgUp: {
    [theme.breakpoints.up(ICON_ONLY_NAVIGATION_BREAKPOINT)]: {
      display:"none !important"
    }
  },
  hideMdDown: {
    [theme.breakpoints.down(ICON_ONLY_NAVIGATION_BREAKPOINT)]: {
      display:"none !important"
    }
  },
  hideSmDown: {
    [theme.breakpoints.down('sm')]: {
      display: "none !important",
    },
  },
  hideXsDown: {
    [theme.breakpoints.down('xs')]: {
      display: "none !important",
    },
  },
  hideSmUp: {
    [theme.breakpoints.up('sm')]: {
      display: "none !important",
    },
  },
  hideMdUp: {
    [theme.breakpoints.up('md')]: {
      display: "none !important",
    },
  },
  rightHeaderItems: {
    marginRight: -8,
    marginLeft: "auto",
    display: "flex",
  },
  // Prevent rearranging of mobile header when search loads after SSR
  searchSSRStandin: {
    minWidth: 48
  },
  headroom: {
    // Styles for header scrolling, provided by react-headroom
    // https://github.com/KyleAMathews/react-headroom
    "& .headroom": {
      top: "unset",
      left: 0,
      right: 0,
      zIndex: 1300,
    },
    "& .headroom--unfixed": {
      position: "relative",
      transform: "translateY(0)",
    },
    "& .headroom--scrolled": {
      transition: "transform 200ms ease-in-out",
    },
    "& .headroom--unpinned": {
      position: "fixed",
      transform: "translateY(-100%)",
    },
    "& .headroom--pinned": {
      position: "fixed",
      transform: "translateY(0%)",
    },
  },
  headroomPinnedOpen: {
    "& .headroom--unpinned": {
      transform: "none !important",
    },
    "& .headroom--unfixed": {
      position: "fixed !important",
    },
  },
  reserveSpaceForLlmChatSidebar: {
    [theme.breakpoints.up("lg")]: {
      "& .headroom": {
        width: `calc(100% - ${LLM_CHAT_SIDEBAR_WIDTH}px)`,
      },
    }
  },
  headerHeight: {
    "--header-height": `${getHeaderHeight()}px`,
    [theme.breakpoints.down('xs')]: {
      "--header-height": `${getMobileHeaderHeight()}px`,
    },
  },
  headerHeightWithBanner: {
    "--header-height": `${getHeaderHeight() + FUNDRAISER_BANNER_HEIGHT}px`,
    [theme.breakpoints.down('xs')]: {
      "--header-height": `${getMobileHeaderHeight() + FUNDRAISER_BANNER_HEIGHT_MOBILE}px`,
    },
  },
}));

const UsersCurrentUpdateMutation = gql(`
  mutation updateUserLayout($selector: SelectorInput!, $data: UpdateUserDataInput!) {
    updateUser(selector: $selector, data: $data) {
      data {
        ...UsersCurrent
      }
    }
  }
`);

const subscribeHydration = () => () => {};
const getHydratedSnapshot = () => true;
const getServerHydratedSnapshot = () => false;

/**
 * The ToC drawer (mobile-specific) uses SidebarsContext, but this is updated on load in a
 * component in a different suspense boundary, which by default would cause SSR mismatches.
 * Prevent SSR mismatch by always rendering the non-ToC version first.
 */
function useGetToC() {
  const sidebars = useContext(SidebarsContext);
  const isHydrated = useSyncExternalStore(
    subscribeHydration,
    getHydratedSnapshot,
    getServerHydratedSnapshot,
  );
  return isHydrated ? sidebars?.toc : null;
}

const Header = ({
  standaloneNavigationPresent,
  stayAtTop=false,
  searchResultsArea,
  backgroundColor,
}: {
  standaloneNavigationPresent: boolean,
  stayAtTop?: boolean,
  searchResultsArea: React.RefObject<HTMLDivElement|null>,
  // CSS var corresponding to the background color you want to apply (see also appBarDarkBackground above)
  backgroundColor?: string,
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const [navigationOpen, setNavigationOpenState] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationHasOpened, setNotificationHasOpened] = useState(false);
  const [searchOpen, setSearchOpenState] = useState(false);
  const [unFixed, setUnFixed] = useState(true);
  const { showFundraiserBanner } = useContext(HeaderHeightContext);
  const getCurrentUser = useGetCurrentUser();
  const currentUserId = useCurrentUserId();
  const isLoggedIn = !!currentUserId;
  const toc = useGetToC();
  const { captureEvent } = useTracking()
  const { notificationsOpened } = useUnreadNotifications();
  const { pathname, hash } = useLocation();
  let headerStyle = { ...(backgroundColor ? { backgroundColor } : {}) };

  const { hideNavigationSidebar, setHideNavigationSidebar } = use(HideNavigationSidebarContext)!;
  const [updateUserNoCache] = useMutationNoCache(UsersCurrentUpdateMutation);
  const toggleStandaloneNavigation = useCallback(() => {
    if (currentUserId) {
      void updateUserNoCache({
        variables: {
          selector: { _id: currentUserId },
          data: {
            hideNavigationSidebar: !hideNavigationSidebar
          }
        }
      })
    }
    setHideNavigationSidebar(!hideNavigationSidebar);
  }, [updateUserNoCache, currentUserId, setHideNavigationSidebar, hideNavigationSidebar]);


  useEffect(() => {
    // When we move to a different page we will be positioned at the top of
    // the page (unless the hash is set) but Headroom doesn't run this callback
    // on navigation so we have to do it manually
    if (!hash) {
      setUnFixed(true);
    }
  }, [pathname, hash]);

  const setNavigationOpen = (open: boolean) => {
    setNavigationOpenState(open);
    captureEvent("navigationBarToggle", {open: open})
  }

  const handleSetNotificationDrawerOpen = useCallback(async (isOpen: boolean): Promise<void> => {
    if (!isLoggedIn) return;
    if (isOpen) {
      setNotificationOpen(true);
      setNotificationHasOpened(true);
      await notificationsOpened();
    } else {
      setNotificationOpen(false);
    }
  }, [isLoggedIn, notificationsOpened]);

  const handleNotificationToggle = () => {
    const currentUser = getCurrentUser()!
    if (!currentUser) return;
    const { lastNotificationsCheck } = currentUser;

    captureEvent("notificationsIconToggle", {
      open: !notificationOpen,
      previousCheck: lastNotificationsCheck,
    });
    void handleSetNotificationDrawerOpen(!notificationOpen);
  }

  // We do two things when the search is open:
  //  1) Pin the header open with the Headroom component
  //  2) Hide the username on mobile so users with long usernames can still
  //     enter search queries
  // Called by SearchBar.
  const setSearchOpen = useCallback((isOpen: boolean) => {
    if (isOpen) { captureEvent("searchToggle", {"open": isOpen}) }
    setSearchOpenState(isOpen);
  }, [captureEvent]);

  const navigationMenuButton = (
    // The navigation menu button either toggles a free floating sidebar, opens
    // a drawer with site navigation, or a drawer with table of contents. (This
    // is structured a little oddly because the hideSmDown/hideMdUp filters
    // cause a misalignment if they're in the wrong part of the tree.)
    <React.Fragment>
      {toc?.sectionData?.sections
        ? <>
            <div className={classes.hideSmDown}>
              <IconButton
                className={classNames(
                  classes.menuButton,
                  {[classes.hideLgUp]: standaloneNavigationPresent && unFixed}
                )}
                color="inherit"
                aria-label="Menu"
                onClick={()=>setNavigationOpen(true)}
              >
                <ForumIcon icon="Menu" className={classes.icon} />
              </IconButton>
            </div>
            <div className={classes.hideMdUp}>
              <IconButton
                className={classNames(
                  classes.menuButton,
                  {[classes.hideLgUp]: standaloneNavigationPresent && unFixed}
                )}
                color="inherit"
                aria-label="Menu"
                onClick={()=>setNavigationOpen(true)}
              >
                <TocIcon className={classes.icon} />
              </IconButton>
            </div>
          </>
        : <IconButton
            className={classNames(
              classes.menuButton,
              {[classes.hideLgUp]: standaloneNavigationPresent && unFixed}
            )}
            color="inherit"
            aria-label="Menu"
            onClick={()=>setNavigationOpen(true)}
          >
            <ForumIcon icon="Menu" className={classes.icon} />
          </IconButton>
      }
      {standaloneNavigationPresent && unFixed && <IconButton
        className={classNames(
          classes.menuButton,
          classes.hideMdDown
        )}
        color="inherit"
        aria-label="Menu"
        onClick={toggleStandaloneNavigation}
      >
        <ForumIcon icon="Menu" className={classes.icon} />
      </IconButton>}
    </React.Fragment>
  )

  // the items on the right-hand side (search, notifications, user menu, login/sign up buttons)
  const rightHeaderItemsNode = <div className={classNames(classes.rightHeaderItems)}>
    <SearchBar onSetIsActive={setSearchOpen} searchResultsArea={searchResultsArea} />

    {!isLoggedIn && <LWUsersAccountMenu />}

    {isLoggedIn && <>
      <div className={searchOpen ? classes.hideMdDown : undefined}>
        <AnalyticsContext pageSectionContext="usersMenu">
          <UsersMenu />
        </AnalyticsContext>
      </div>
      <KarmaChangeNotifier
        onOpen={() => void handleSetNotificationDrawerOpen(false)}
        notificationOpen={notificationOpen}
      />
      <NotificationsMenuButton
        toggle={handleNotificationToggle}
        open={notificationOpen}
      />
    </>}
  </div>

  // the left side nav menu
  const headerNavigationDrawer = <NavigationDrawer
    open={navigationOpen}
    handleOpen={() => setNavigationOpen(true)}
    handleClose={() => setNavigationOpen(false)}
    toc={toc?.sectionData ?? null}
  />

  // the right side notifications menu
  const headerNotificationsMenu = isLoggedIn && (
    <NotificationsMenu
      open={notificationOpen}
      hasOpened={notificationHasOpened}
      setIsOpen={handleSetNotificationDrawerOpen}
    />
  );

  // Adjust header width when LLM chat sidebar is open and header is fixed
  const llmChatSidebarOpen = useContext(IsLlmChatSidebarOpenContext);

  // Make all the text and icons the same color as the text on the current forum event banner
  const useContrastText = Object.keys(headerStyle).includes('backgroundColor') || Object.keys(headerStyle).includes('background');

  const isGrayBackground = useIsOnGrayBackground();

  return (
    <AnalyticsContext pageSectionContext="header">
      <div className={classes.root}>
        <Headroom
          disableInlineStyles
          downTolerance={1} upTolerance={1}
          height={showFundraiserBanner ? getHeaderHeight() + FUNDRAISER_BANNER_HEIGHT : getHeaderHeight()}
          className={classNames(classes.headroom, {
            [classes.headroomPinnedOpen]: searchOpen,
            [classes.reserveSpaceForLlmChatSidebar]: llmChatSidebarOpen && !unFixed,
          })}
          onUnfix={() => setUnFixed(true)}
          onUnpin={() => setUnFixed(false)}
          disable={stayAtTop}
        >
          {showFundraiserBanner && <FundraiserBanner />}
          <header
            className={classNames(
              classes.appBar,
              useContrastText && classes.appBarDarkBackground,
              !isGrayBackground && classes.blackBackgroundAppBar,
            )}
            style={headerStyle}
          >
            <div className={classes.toolbar}>
              {navigationMenuButton}

              <Typography className={classNames(classes.title, classes.hideXsDown)} variant="title">
                <Link to="/" className={classes.titleLink}>
                  {forumHeaderTitleSetting.get(forumType)}
                </Link>
                <HeaderSubtitle />
              </Typography>
              <Typography className={classNames(classes.title, classes.hideSmUp)} variant="title">
                <Link to="/" className={classes.titleLink}>
                  {forumShortTitleSetting.get(forumType)}
                </Link>
              </Typography>
              {rightHeaderItemsNode}
            </div>
          </header>
          {headerNavigationDrawer}
        </Headroom>
        {headerNotificationsMenu}
      </div>
    </AnalyticsContext>
  )
}

export const HeaderHeightProvider = ({ children }: { children: React.ReactNode }) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const [cookies] = useCookiesWithConsent([HIDE_FUNDRAISER_BANNER_COOKIE]);
  const hideFundraiserBanner = cookies[HIDE_FUNDRAISER_BANNER_COOKIE] === "true";
  const pathname = usePrerenderablePathname();
  const isFrontPage = isHomeRoute(pathname, forumType);
  const showFundraiserBanner = false; // !hideFundraiserBanner && isFrontPage;
  const value = useMemo<HeaderHeightContextValue>(() => ({ showFundraiserBanner, }), [showFundraiserBanner]);

  return (
    <HeaderHeightContext.Provider value={value}>
      <span className={classNames(classes.headerHeight, {
        [classes.headerHeightWithBanner]: showFundraiserBanner,
      })}>
        {children}
      </span>
    </HeaderHeightContext.Provider>
  );
};

export default registerComponent('Header', Header, {
  areEqual: "auto",
  hocs: [withErrorBoundary]
});
